import { applyThreadParentPatch } from './threadParentPatch';

/**
 * Merge a single incoming message into a cached message array, deduplicating
 * by id (incoming overlays existing fields on collision) and applying the
 * thread-parent metadata patch when the incoming message is a NEW thread
 * reply (incrementing the parent's `threadReplyCount` + bumping
 * `threadLastReplyAt`).
 *
 * Single source of truth for every code path that writes to the
 * `GET_MESSAGES_PAGINATED` Apollo cache:
 *   - Sender's mutation `update(cache, { data })` in MessageComposer (V1).
 *   - Sender's mutation `update(cache, { data })` in MessageComposerV2.
 *   - Receiver's `ON_NEW_MESSAGE` subscription handler in UnifiedChatPanel.
 *
 * Why this exists (2026-05-15, BH): prior to consolidation, three copies of
 * the merge logic had drifted. The two sender paths patched the parent's
 * thread metadata on a new thread reply, but the receiver subscription path
 * did NOT — so when User A sent a thread reply, User B (with the channel
 * open) saw the new reply in the thread panel but the parent's "N replies"
 * indicator on the main timeline did not increment until a hard refresh.
 * The Apollo re-emit then overwrote B's locally-patched parent via
 * `mergeServerAndLocalMessages` (server-prefers-on-id semantics by design).
 *
 * @param {Array<{id: string}>} existingMessages
 * @param {{id: string, threadParentId?: string|null}} newMsg
 * @returns {Array<{id: string}>}
 */
export function mergeMessageIntoCache(existingMessages, newMsg) {
  if (!Array.isArray(existingMessages)) return existingMessages;
  if (!newMsg || typeof newMsg.id !== 'string') return existingMessages;

  const idx = existingMessages.findIndex((m) => m.id === newMsg.id);
  const isNew = idx < 0;
  const merged = isNew
    ? [...existingMessages, newMsg]
    : existingMessages.map((m) => (m.id === newMsg.id ? { ...m, ...newMsg } : m));

  if (isNew && newMsg.threadParentId) {
    return applyThreadParentPatch(merged, newMsg);
  }
  return merged;
}
